import {
  convertDateRangesToMarkdown,
  findTimelineGaps,
  getApplicantRequiredDuration,
  getThresholdYears
} from 'helpers/date';
import { getDuplicateItemsList } from 'helpers/employment';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { ValidateJSModel } from 'forms/shared/types/validatejs';
import { hasYesOrNo, validateModel } from 'models/validate';
import { DateRangeModel } from 'forms/shared/types';
import { employmentAddressNameFields } from 'constants/enums/people';
import { DeepSafePartial } from 'types/types';
import { formIncludesPvqPartB, isPVQForm } from 'helpers/form';
import { historyEmploymentItemValidator, pvqHistoryEmploymentItemValidator } from './validators/item';
import { HistoryEmploymentModel } from './types';
import pvqForeignEmploymentItemValidator from './validators/pvqForeignEmploymentItemValidator';

const historyEmploymentValidator: ValidateJSModel<HistoryEmploymentModel> = {
  EmploymentRecord: {
    presence: true,
    hasValue: {
      validator: { inclusion: ['No'] },
    },
  },
  List: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options.formType);
    const duplicateItems = getDuplicateItemsList(value?.items || []);
    const presentChecked = value?.items?.some(
      (element) => element?.Item?.Dates?.present === true
    );
    let additionalValidation = duplicateItems.length > 0 ? {
      requireTrue: { // force error message; value will never be true
        message: 'history.employment.errors.duplicateListItems',
      }
    } : {};

    if (!additionalValidation?.requireTrue) {
      const { errors = [] } = getDuplicateNamesAndAddresses(value?.items, options?.peopleWhoKnowYou, employmentAddressNameFields);
      if (errors.length > 0) {
        additionalValidation = {
          requireTrue: { // force error message; value will never be true
            message: 'relationships.people.error.duplicateListItems',
          }
        };
      }
    }

    return {
      presence: true,
      ...additionalValidation,
      accordion: { validator: isPVQ ? pvqHistoryEmploymentItemValidator : historyEmploymentItemValidator },
      durationCoverage: (value2, attributes2, attributeName2, options2) => {
        const { requireYears, applicantBirthdate, formType } = options2;
        const thresholdYears = getThresholdYears(formType);
        const { coverage, requiredYears: years } = getApplicantRequiredDuration({
          requiredDuration: requireYears ? { years: requireYears } : undefined,
          applicantBirthdate,
          thresholdYears,
          type: 'employment'
        });
        const context = {
          requiredDuration: coverage,
          incompleteDuration: {
            code: 'error.gap.historyEmployment',
            localeOptions: {
              messageParameters: {
                years,
                gaps: convertDateRangesToMarkdown(
                  findTimelineGaps(
                    coverage,
                    value2?.items?.map((i) => i?.Item?.Dates) ?? []
                  )
                ),
              },
            },
          },
        };

        if (value2 === undefined) { return context; }

        const { items } = value2;

        // This is for additional years employed at a particular company
        let additionalDates: DeepSafePartial<DateRangeModel[]> = [];
        if (attributes2 && attributes2.EmploymentRecord) {
          // this foreach method updates additional dates
          items?.forEach((element) => {
            if (element?.Item?.Additional?.items) {
              const employmentDates = element?.Item.Additional.items
                .filter(
                  (d) => d?.Item?.DatesEmployed
                    && validateModel(
                      { Dates: d?.Item.DatesEmployed },
                      { Dates: { presence: true, daterange: true } },
                      { ...options2 }
                    ) === true
                )
                .map((d) => d?.Item?.DatesEmployed);
              additionalDates = additionalDates.concat(employmentDates);
            }
          });
        }

        return {
          requirePresent: !presentChecked,
          requirePresentMessage: 'error.presentUnchecked.historyEmployment',
          ...context,
          incompleteDuration: {
            ...context.incompleteDuration,
            localeOptions: {
              ...context.incompleteDuration.localeOptions,
              messageParameters: {
                ...context.incompleteDuration.localeOptions.messageParameters,
                gaps: convertDateRangesToMarkdown(
                  findTimelineGaps(coverage, additionalDates)
                ),
              },
            },
          },
          additionalDates,
        };
      },
    };
  },
  HasForeignService: (value, attributes, attributeName, options) => {
    if (formIncludesPvqPartB(options?.formType)) {
      return {
        presence: true,
        hasValue: { validator: hasYesOrNo }
      };
    }

    return {};
  },
  ForeignServiceList: (value, attributes, attributeName, options) => {
    if (formIncludesPvqPartB(options?.formType) && attributes?.HasForeignService?.value === 'Yes') {
      return {
        presence: true,
        accordion: { validator: pvqForeignEmploymentItemValidator },
      };
    }

    return {};
  },
};

export default historyEmploymentValidator;
